import { Component, Input, OnChanges } from '@angular/core';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-comm-line-chart',
  templateUrl: './comm-line-chart.component.html',
  styleUrls: ['./comm-line-chart.component.scss']
})
export class CommLineChartComponent implements OnChanges {
  @Input() data: any;
  @Input() id: string = 'comm-line-chart';
  @Input() title: string = 'Activity Chart';
  @Input() subtitle: string = '';
  @Input() accentColor: string = '#0071e3';
  @Input() accentColorSecondary: string = '#ff9500';
  @Input() iconClass: string = 'fas fa-phone';

  viewMode: 'yearly' | 'daily' = 'yearly';
  selectedMonth: string = '';
  chartInstance: any;

  totalYearly: number = 0;
  avgMonthly: number = 0;
  peakMonth: string = '';
  peakDay: string = '';

  readonly months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  ngOnChanges() {
    if (this.data) {
      this._initDefaults();
      setTimeout(() => this.renderChart(), 120);
    }
  }

  private _initDefaults() {
    // Pick current month as default for daily view
    const currentMonth = new Date().toLocaleString('default', { month: 'short' });
    const availableMonths = this.getMonthOptions();
    if (!this.selectedMonth || !availableMonths.includes(this.selectedMonth)) {
      this.selectedMonth = availableMonths.includes(currentMonth) ? currentMonth : (availableMonths[0] || 'Jan');
    }

    // Compute summary stats from yearly data
    const yearlyData = this.data?.yearly || {};
    const values: number[] = this.months.map(m => Number(yearlyData[m] ?? 0));
    this.totalYearly = values.reduce((a, b) => a + b, 0);
    this.avgMonthly = this.totalYearly > 0 ? Math.round(this.totalYearly / 12) : 0;
    const maxVal = Math.max(...values);
    const maxIdx = values.indexOf(maxVal);
    this.peakMonth = maxVal > 0 ? this.months[maxIdx] : '—';
  }

  renderChart() {
    if (!this.data) return;

    const canvasId = 'canvas__' + this.id;
    const ctx: any = document.getElementById(canvasId);
    if (!ctx) return;

    const context = ctx.getContext('2d');

    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }

    let labels: string[] = [];
    let values: number[] = [];

    if (this.viewMode === 'yearly') {
      labels = this.months;
      const yearlyData = this.data?.yearly || {};
      values = this.months.map(m => Number(yearlyData[m] ?? 0));
    } else {
      const dailyData = this.data?.daily?.[this.selectedMonth] || {};
      const monthIndex = new Date(`${this.selectedMonth} 1, 2025`).getMonth();
      const daysInMonth = new Date(2025, monthIndex + 1, 0).getDate();
      labels = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString().padStart(2, '0'));
      values = labels.map(day => Number(dailyData[day] ?? 0));
    }

    // Build gradient
    const gradient = context.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, this._hexToRgba(this.accentColor, 0.35));
    gradient.addColorStop(1, this._hexToRgba(this.accentColor, 0.02));

    this.chartInstance = new Chart(context, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: this.title,
          data: values,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointBackgroundColor: this.accentColor,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          borderColor: this.accentColor,
          borderWidth: 2.5,
          backgroundColor: gradient,
          fill: true,
          tension: 0.45,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 900, easing: 'easeInOutQuart' },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(255,255,255,0.95)',
            borderColor: 'rgba(0,0,0,0.08)',
            borderWidth: 1,
            titleColor: '#1d1d1d',
            bodyColor: '#595959',
            padding: 12,
            cornerRadius: 12,
            boxPadding: 6,
            titleFont: { size: 13, weight: 700 as any, family: '-apple-system, BlinkMacSystemFont, sans-serif' },
            bodyFont: { size: 12, family: '-apple-system, BlinkMacSystemFont, sans-serif' },
          },
          filler: { propagate: false }
        },
        scales: {
          y: {
            grid: { color: 'rgba(0,0,0,0.05)', lineWidth: 1 },
            border: { display: false, dash: [4, 4] } as any,
            ticks: {
              color: '#86868b',
              font: { size: 11, family: '-apple-system, BlinkMacSystemFont, sans-serif' },
              padding: 8,
            },
            beginAtZero: true,
          },
          x: {
            grid: { display: false },
            border: { display: false } as any,
            ticks: {
              color: '#86868b',
              font: { size: 11, family: '-apple-system, BlinkMacSystemFont, sans-serif' },
              padding: 8,
            }
          }
        }
      }
    });
  }

  setView(mode: 'yearly' | 'daily') {
    this.viewMode = mode;
    setTimeout(() => this.renderChart(), 80);
  }

  onMonthChange(event: any) {
    this.selectedMonth = event.target.value;
    this.renderChart();
  }

  getMonthOptions(): string[] {
    return Object.keys(this.data?.daily || {});
  }

  private _hexToRgba(hex: string, alpha: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(0,113,227,${alpha})`;
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
}
